<script setup>
import { ref, computed, onMounted } from 'vue';
import ReservationPlansAPI from 'dashboard/api/reservationPlans';
import ReservationSettingsAPI from 'dashboard/api/reservationSettings';
import AgentsAPI from 'dashboard/api/agents';
import BranchesAPI from 'dashboard/api/branches';

// Métodos de pago disponibles (etiquetas filtrables en el modal de planes).
const PAYMENT_METHODS = ['Efectivo', 'Transferencia', 'Tarjeta'];

// Tipos de plan con la etiqueta del nombre sugerida según el tipo.
const CATEGORIES = [
  { value: 'quince', label: 'Quinceañera', nameLabel: 'Nombre de la quinceañera' },
  { value: 'ninos', label: 'Niños/as', nameLabel: 'Nombre del niño/a' },
  { value: 'boda', label: 'Boda', nameLabel: 'Nombre de los novios' },
  { value: 'embarazo', label: 'Embarazo', nameLabel: 'Nombre de la futura mamá' },
  { value: 'graduacion', label: 'Graduación', nameLabel: 'Nombre del graduando/a' },
  { value: 'familiar', label: 'Familiar', nameLabel: 'Nombre de la familia' },
  { value: 'empresarial', label: 'Empresarial', nameLabel: 'Nombre de la empresa' },
  { value: 'madre', label: 'Madre', nameLabel: 'Nombre de la madre' },
  { value: 'recien_nacido', label: 'Recién Nacido', nameLabel: 'Nombre del bebé' },
  { value: 'cumpleanos', label: 'Cumpleaños', nameLabel: 'Nombre del cumpleañero/a' },
  { value: 'mellizas', label: 'XV Mellizas', nameLabel: 'Nombre de las quinceañeras' },
  { value: 'basico', label: 'Básico / Estudio', nameLabel: 'Nombre del cliente' },
  { value: 'otro', label: 'Otro', nameLabel: 'Nombre del cliente' },
];

// Encargada de confirmación (única persona que puede mover el lead fuera de
// "Por confirmar + pendiente facturar").
const agents = ref([]);
const confirmerId = ref('');
const savingConfirmer = ref(false);

const fetchConfirmer = async () => {
  try {
    const [agentsRes, settingsRes] = await Promise.all([
      AgentsAPI.get(),
      ReservationSettingsAPI.get(),
    ]);
    agents.value = agentsRes.data || [];
    confirmerId.value = settingsRes.data?.confirmer_user_id || '';
  } catch (e) {
    agents.value = [];
  }
};

const saveConfirmer = async () => {
  savingConfirmer.value = true;
  try {
    await ReservationSettingsAPI.save({ confirmer_user_id: confirmerId.value || '' });
  } catch (e) {
    /* noop */
  } finally {
    savingConfirmer.value = false;
  }
};

const plans = ref([]);
const loading = ref(false);
const saving = ref(false);
const showForm = ref(false);
const form = ref(emptyForm());

const branches = ref([]);

function emptyForm() {
  return {
    id: null, name: '', category: 'quince', price: '', name_label: '',
    photos: '', minutes: '', scenarios: '', scenarios_up_to: false,
    outfits: '', max_people: '', payment_methods: [...PAYMENT_METHODS],
    branch_ids: [], featured: false,
  };
}

const togglePayment = m => {
  const i = form.value.payment_methods.indexOf(m);
  if (i === -1) form.value.payment_methods.push(m);
  else form.value.payment_methods.splice(i, 1);
};
const toggleBranch = id => {
  const i = form.value.branch_ids.indexOf(id);
  if (i === -1) form.value.branch_ids.push(id);
  else form.value.branch_ids.splice(i, 1);
};

const categoryLabel = value =>
  CATEGORIES.find(c => c.value === value)?.label || value;

const fmtPrice = p => {
  const n = Number(p) || 0;
  return `RD$ ${n.toLocaleString('es-DO', { maximumFractionDigits: 0 })}`;
};

const fetchPlans = async () => {
  loading.value = true;
  try {
    const { data } = await ReservationPlansAPI.get();
    plans.value = data || [];
  } catch (e) {
    plans.value = [];
  } finally {
    loading.value = false;
  }
};

const onCategoryChange = () => {
  const suggested = CATEGORIES.find(c => c.value === form.value.category)?.nameLabel;
  // Solo autocompleta la etiqueta si está vacía o coincide con otra sugerencia.
  const isSuggestion = CATEGORIES.some(c => c.nameLabel === form.value.name_label);
  if (!form.value.name_label || isSuggestion) form.value.name_label = suggested || '';
};

const openNew = () => {
  form.value = emptyForm();
  onCategoryChange();
  showForm.value = true;
};

const openEdit = plan => {
  form.value = {
    id: plan.id,
    name: plan.name,
    category: plan.category || 'otro',
    price: plan.price,
    name_label: plan.name_label || '',
    photos: plan.photos ?? '',
    minutes: plan.minutes ?? '',
    scenarios: plan.scenarios ?? '',
    scenarios_up_to: !!plan.scenarios_up_to,
    outfits: plan.outfits ?? '',
    max_people: plan.max_people ?? '',
    payment_methods: Array.isArray(plan.payment_methods) && plan.payment_methods.length
      ? [...plan.payment_methods] : [...PAYMENT_METHODS],
    branch_ids: Array.isArray(plan.branch_ids) ? [...plan.branch_ids] : [],
    featured: !!plan.featured,
  };
  showForm.value = true;
};

const cancelForm = () => {
  showForm.value = false;
  form.value = emptyForm();
};

const canSave = computed(
  () => form.value.name.trim() && form.value.price !== '' && !saving.value
);

const savePlan = async () => {
  if (!canSave.value) return;
  saving.value = true;
  const numOrNull = v => (v === '' || v === null ? null : Number(v));
  const payload = {
    name: form.value.name.trim(),
    category: form.value.category,
    price: Number(form.value.price) || 0,
    name_label: form.value.name_label.trim(),
    photos: numOrNull(form.value.photos),
    minutes: numOrNull(form.value.minutes),
    scenarios: numOrNull(form.value.scenarios),
    scenarios_up_to: form.value.scenarios_up_to,
    outfits: numOrNull(form.value.outfits),
    max_people: numOrNull(form.value.max_people),
    payment_methods: form.value.payment_methods,
    branch_ids: form.value.branch_ids,
    featured: form.value.featured,
  };
  try {
    if (form.value.id) {
      await ReservationPlansAPI.update(form.value.id, payload);
    } else {
      await ReservationPlansAPI.create(payload);
    }
    await fetchPlans();
    cancelForm();
  } catch (e) {
    /* noop: se queda el formulario abierto para reintentar */
  } finally {
    saving.value = false;
  }
};

const deletePlan = async plan => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(`¿Eliminar el plan "${plan.name}"?`)) return;
  try {
    await ReservationPlansAPI.delete(plan.id);
    await fetchPlans();
  } catch (e) {
    /* noop */
  }
};

const fetchBranches = async () => {
  try {
    const { data } = await BranchesAPI.get();
    branches.value = data || [];
  } catch (e) {
    branches.value = [];
  }
};

onMounted(() => {
  fetchPlans();
  fetchConfirmer();
  fetchBranches();
});
</script>

<template>
  <div class="w-full bg-n-background">
    <div class="px-10 py-6">
      <!-- Header -->
      <div class="flex items-end justify-between">
        <div>
          <h1 class="text-2xl font-semibold text-n-slate-12">Catálogo de Planes</h1>
          <p class="text-sm text-n-slate-11 mt-1.5">
            Los planes que reserva el cliente, con su precio y la etiqueta del nombre según el tipo.
            El formulario de reserva los usa para autollenar.
          </p>
        </div>
        <button
          class="px-4 py-2 rounded-lg bg-amber-500 text-n-background text-sm font-semibold hover:bg-amber-400 transition"
          @click="openNew"
        >
          + Agregar plan
        </button>
      </div>

      <!-- Encargada de confirmación -->
      <div class="mt-5 p-4 rounded-xl bg-n-solid-1 border border-n-weak flex items-center gap-4 flex-wrap">
        <div class="flex-1 min-w-[260px]">
          <div class="text-sm font-semibold text-n-slate-12">Encargada de confirmación</div>
          <div class="text-xs text-n-slate-11 mt-1">
            Única persona que puede mover un lead fuera de “Por confirmar + pendiente facturar”.
            El resto (incluido el agente) no puede moverlo. Los super-admin siempre pueden.
          </div>
        </div>
        <select
          v-model="confirmerId"
          class="px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500 min-w-[200px]"
          :disabled="savingConfirmer"
          @change="saveConfirmer"
        >
          <option value="">Sin encargada (sin candado)</option>
          <option v-for="a in agents" :key="a.id" :value="a.id">{{ a.name }}</option>
        </select>
      </div>

      <!-- Form -->
      <div
        v-if="showForm"
        class="mt-5 p-5 rounded-xl bg-n-solid-1 border border-n-weak"
      >
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="md:col-span-2">
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">Nombre del plan *</label>
            <input
              v-model="form.name"
              type="text"
              placeholder="Ej. Plan H · XV Exterior"
              class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500"
            />
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">Tipo *</label>
            <select
              v-model="form.category"
              class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500"
              @change="onCategoryChange"
            >
              <option v-for="c in CATEGORIES" :key="c.value" :value="c.value">{{ c.label }}</option>
            </select>
          </div>
          <div>
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">Precio (RD$) *</label>
            <input
              v-model="form.price"
              type="number"
              min="0"
              placeholder="55000"
              class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500"
            />
          </div>
          <div class="md:col-span-2">
            <label class="block text-xs font-medium text-n-slate-11 mb-1.5">
              Etiqueta del nombre
              <span class="text-n-slate-10">(se sugiere según el tipo · editable)</span>
            </label>
            <input
              v-model="form.name_label"
              type="text"
              placeholder="Nombre de la quinceañera"
              class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500"
            />
          </div>

          <!-- Detalles del flyer (para el modal de Planes) -->
          <div class="md:col-span-2 mt-1 pt-4 border-t border-n-weak">
            <div class="text-[11px] uppercase tracking-wide font-semibold text-amber-500 mb-3">📸 Detalles del plan (para el catálogo)</div>
            <div class="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1.5">🖼 Fotografías</label>
                <input v-model="form.photos" type="number" min="0" placeholder="5" class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1.5">🕐 Minutos de sesión</label>
                <input v-model="form.minutes" type="number" min="0" placeholder="20" class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1.5">👗 Vestimentas</label>
                <input v-model="form.outfits" type="number" min="0" placeholder="1" class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1.5">🖥 Escenarios</label>
                <input v-model="form.scenarios" type="number" min="0" placeholder="1" class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500" />
              </div>
              <div>
                <label class="block text-xs font-medium text-n-slate-11 mb-1.5">👥 Máx. personas</label>
                <input v-model="form.max_people" type="number" min="0" placeholder="1" class="w-full px-3 py-2 rounded-lg bg-n-background border border-n-weak text-sm text-n-slate-12 outline-none focus:border-amber-500" />
              </div>
              <label class="flex items-center gap-2 text-sm text-n-slate-11 self-end pb-2 cursor-pointer">
                <input v-model="form.scenarios_up_to" type="checkbox" class="accent-amber-500" />
                Decir "Hasta N escenarios"
              </label>
            </div>

            <div class="mt-4">
              <label class="block text-xs font-medium text-n-slate-11 mb-2">💳 Métodos de pago</label>
              <div class="flex gap-2 flex-wrap">
                <button
                  v-for="m in PAYMENT_METHODS"
                  :key="m"
                  type="button"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium border transition"
                  :class="form.payment_methods.includes(m) ? 'bg-amber-500/15 text-amber-500 border-amber-500/50' : 'text-n-slate-11 border-n-weak'"
                  @click="togglePayment(m)"
                >{{ m }}</button>
              </div>
            </div>

            <div class="mt-4">
              <label class="block text-xs font-medium text-n-slate-11 mb-2">📍 Sucursales donde aplica <span class="text-n-slate-10">(ninguna = todas)</span></label>
              <div class="flex gap-2 flex-wrap">
                <button
                  v-for="b in branches"
                  :key="b.id"
                  type="button"
                  class="px-3 py-1.5 rounded-lg text-xs font-medium border transition"
                  :class="form.branch_ids.includes(b.id) ? 'bg-amber-500/15 text-amber-500 border-amber-500/50' : 'text-n-slate-11 border-n-weak'"
                  @click="toggleBranch(b.id)"
                >{{ b.name }}</button>
              </div>
            </div>

            <label class="flex items-center gap-2 text-sm text-n-slate-11 mt-4 cursor-pointer">
              <input v-model="form.featured" type="checkbox" class="accent-amber-500" />
              ⭐ Marcar como plan destacado / recomendado
            </label>
          </div>
        </div>
        <div class="flex items-center gap-3 mt-5">
          <button
            class="px-4 py-2 rounded-lg bg-amber-500 text-n-background text-sm font-semibold hover:bg-amber-400 transition disabled:opacity-40"
            :disabled="!canSave"
            @click="savePlan"
          >
            {{ form.id ? 'Guardar cambios' : 'Crear plan' }}
          </button>
          <button
            class="px-4 py-2 rounded-lg border border-n-weak text-sm text-n-slate-11 hover:text-n-slate-12 transition"
            @click="cancelForm"
          >
            Cancelar
          </button>
        </div>
      </div>

      <!-- List -->
      <div class="mt-6 rounded-xl border border-n-weak overflow-hidden">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-10 text-[11px] uppercase tracking-wide border-b border-n-weak">
              <th class="px-4 py-3 font-medium">Plan</th>
              <th class="px-4 py-3 font-medium">Tipo</th>
              <th class="px-4 py-3 font-medium">Precio</th>
              <th class="px-4 py-3 font-medium">Etiqueta del nombre</th>
              <th class="px-4 py-3 font-medium text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="loading">
              <td colspan="5" class="px-4 py-10 text-center text-n-slate-11">Cargando…</td>
            </tr>
            <tr v-else-if="!plans.length">
              <td colspan="5" class="px-4 py-10 text-center text-n-slate-11">
                Aún no hay planes. Agrega el primero con “+ Agregar plan”.
              </td>
            </tr>
            <tr
              v-for="plan in plans"
              v-else
              :key="plan.id"
              class="border-b border-n-weak/60 hover:bg-n-alpha-black1"
            >
              <td class="px-4 py-3 text-n-slate-12 font-medium">{{ plan.name }}</td>
              <td class="px-4 py-3">
                <span class="text-[11px] px-2 py-0.5 rounded bg-amber-500/12 text-amber-500 border border-amber-500/40">
                  {{ categoryLabel(plan.category) }}
                </span>
              </td>
              <td class="px-4 py-3 text-green-500 font-semibold">{{ fmtPrice(plan.price) }}</td>
              <td class="px-4 py-3 text-n-slate-11">{{ plan.name_label || '—' }}</td>
              <td class="px-4 py-3 text-right whitespace-nowrap">
                <button
                  class="px-2 py-1 rounded text-[12px] text-n-slate-11 hover:text-amber-500 transition"
                  @click="openEdit(plan)"
                >
                  Editar
                </button>
                <button
                  class="px-2 py-1 rounded text-[12px] text-n-slate-11 hover:text-red-500 transition"
                  @click="deletePlan(plan)"
                >
                  Eliminar
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>
